import MuiFab from '@mui/material/Fab';
import { spacing, SpacingProps, styled } from '@mui/system';
import { themeColors } from '../ThemeProvider/AvenyThemeColors';

interface IProps extends SpacingProps {
  children?: NonNullable<React.ReactNode>;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  size: 'small' | 'medium' | 'large';
  color?: 'default' | 'inherit' | 'primary' | 'secondary' | 'error';
  testId?: string;
  boxShadowNone?: boolean;
  disabledButtonStyle?: boolean;
  glass?: boolean;
}
/**
 * Floating Action Button, kallad FAB, är en stor knapp menad att representera en eller maximalt två primära funktioner på en sida.
 * I vårt fall flyter inte knappen som den normalt gör i Material design, utan den är bara en mycket dominant knapp.
 * Exempel på användning är i ekonomisk analys, där den finns mitt på sidan och representerar handlingen att utföra den analys som valts.
 */
const EDFabBase: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { boxShadowNone, disabledButtonStyle, glass, testId, ...restProps } =
    props;
  return (
    <MuiFab
      sx={[
        {
          zIndex: 'auto',
        },
        /* Styles applied to the element if `color="error"`. */
        props.color === 'error' && {
          color: (theme: any) => theme.palette.common.white,
          backgroundColor: (theme: any) => theme.palette.error.main,
          '&:hover': {
            backgroundColor: (theme: any) => theme.palette.error.dark,
            '@media (hover: none)': {
              backgroundColor: (theme: any) => theme.palette.error.main,
            },
          },
        },
        Boolean(props.boxShadowNone) && {
          boxShadow: theme => theme.shadows[0],
        },
        Boolean(props.disabledButtonStyle) && {
          '&:disabled': {
            background: theme => theme.palette.grey[300],
          },
          '&$buttonDisabled:hover': {
            background: theme => theme.palette.grey[300],
          },
        },
        Boolean(props.glass) && {
          '&&': {
            border: `1px solid ${themeColors.customGlass.buttonBorder}`,
            boxShadow: `0px 8px 32px ${themeColors.customGlass.buttonShadow}`,
            backdropFilter: 'blur(24px) saturate(0.8)',
            backgroundColor: themeColors.customGlass.buttonBackground,
            color: themeColors.customGlass.buttonColor,
          },
          '&&:hover': {
            backgroundColor: themeColors.customGlass.buttonBackground,
          },
        },
      ]}
      data-testid={props.testId}
      {...restProps}
    >
      {props.children}
    </MuiFab>
  );
};

const EDFab: React.FC<React.PropsWithChildren<IProps>> = styled(EDFabBase, {
  name: 'EDFab',
})(spacing);

export default EDFab;
